/**
 * Tải ảnh hướng dẫn của FAQ lên storage data4 và trả về link public.
 *
 * Dùng lại endpoint `/web.UploadFile` mà các trang quản trị khác (banner, quà, kho báu…) đang dùng,
 * nên không phát sinh thêm cửa upload nào vào bucket dùng chung.
 */

// Bucket kèm sẵn đường dẫn theo kiểu cũ mà endpoint đang nhận: <bucket>/<thư mục>.
// Ảnh CSKH để riêng một thư mục để sau này rà soát hoặc dọn dẹp không đụng vào ảnh của tính năng khác.
const BUCKET_NAME = 'ikara-data/images/customer-support';

const PUBLIC_BASE_URL = 'https://data4.ikara.co/data/minio/';

const MAX_FILE_SIZE_MB = 10;

const CONTENT_TYPE_BY_EXTENSION = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
};

const randomKey = () =>
    'xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });

const extensionOf = (fileName) => String(fileName || '').split('.').pop().toLowerCase();

/**
 * Chặn ngay ở trình duyệt thay vì để người dùng chờ upload xong mới báo lỗi.
 *
 * @return {string|null} thông báo lỗi, hoặc null nếu file hợp lệ
 */
export const validateImageFile = (file) => {
    if (!file) {
        return 'Chưa chọn file nào.';
    }

    const extension = extensionOf(file.name);
    if (!CONTENT_TYPE_BY_EXTENSION[extension]) {
        return `"${file.name}" không phải ảnh. Chỉ nhận jpg, png, gif, webp.`;
    }

    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
        return `"${file.name}" nặng quá ${MAX_FILE_SIZE_MB}MB.`;
    }

    return null;
};

/**
 * @param {File} file ảnh do người dùng chọn
 * @return {Promise<string>} link public của ảnh sau khi tải lên
 */
export const uploadFaqImage = (file) => {
    const error = validateImageFile(file);
    if (error) {
        return Promise.reject(new Error(error));
    }

    const extension = extensionOf(file.name);
    // Tên file ngẫu nhiên để hai người cùng tải ảnh trùng tên không ghi đè lên nhau.
    const objectKey = `${randomKey()}.${extension}`;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('bucketName', BUCKET_NAME);
    formData.append('objectKey', objectKey);
    formData.append('contentType', CONTENT_TYPE_BY_EXTENSION[extension]);

    return fetch(global.config.apiDomain + '/web.UploadFile', {
        method: 'POST',
        body: formData,
    })
        .then((response) => {
            if (!response.ok) {
                throw new Error(`Tải ảnh thất bại (HTTP ${response.status})`);
            }
            return response.text();
        })
        .then((responseText) => {
            const body = (responseText || '').trim();

            // Endpoint trả về link, nhưng vài phiên bản trả chuỗi rỗng hoặc thông báo ngắn;
            // khi đó tự dựng link theo đúng đường dẫn vừa ghi.
            if (body.startsWith('http')) {
                return body;
            }
            if (body.startsWith('data4.') || body.startsWith('data5.')) {
                return `https://${body}`;
            }
            return PUBLIC_BASE_URL + BUCKET_NAME + '/' + objectKey;
        });
};

/** Tách chuỗi link ngăn nhau bằng dấu phẩy thành mảng, bỏ phần trắng. */
export const splitImageUrls = (value) =>
    String(value || '')
        .split(',')
        .map((url) => url.trim())
        .filter(Boolean);

export const joinImageUrls = (urls) => urls.join(',');
